<template>
  <div>
    <div v-if="isPrgtReport">
      <v-card-text>
        <v-data-table
          :headers="[]"
          :items="prgtTableItems"
          hide-headers
          hide-actions
          class="elevation-1 table"
        >
          <template slot="items" slot-scope="props">
            <td class="extraGrey--text">{{ props.item.key }}</td>
            <td class="extraGrey--text">
              <span v-html="props.item.value"></span>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </div>
    <div v-if="!isPrgtReport">
      <Tab
        v-for="(level, index) in selectedTemplate.loadFunctionSequence"
        :key="index"
        :tabs="dataList[index]"
        :lvl="index"
        @activeTabChange="tabChange"
      />
    </div>
    <Dialog
      v-if="!!this.activeFilter.Date"
      :dialog="isDownloadDialog"
      :dateFrom="this.activeFilter.Date.from"
      :dateTo="this.activeFilter.Date.to"
      :items="currentTabDeviceNames"
      @close="isDownloadDialog = false"
      @download="downloadReport"
    />
    <TableButtons
      :dataTitle="dataTitle"
      @download:report="initiateDownload"
      @reload:report="refreshTable"
    />
    <Graph
      v-if="view == 'graph'"
      :title="dataTitle"
      :series="computedSeries"
      :xAxis="xAxis"
      :yAxis="computedYAxis"
    />
    <RecyclerDataTable
      v-show="view == 'table'"
      :headers="headers"
      :items="dateFormattedTableData"
      :key_field="primaryKey"
      :reduceHeight="computeReduceTableHeight"
      v-if="headers.length > 0"
    />
  </div>
</template>

<script>
const _ = require("lodash");
import moment from "moment";
const momentTz = require("moment-timezone");

import Graph from "@/components/utility/GraphTemplate";
import Tab from "./Tab";

import { mapGetters } from "vuex";
import CustomReport from "@/services/customReport";

export default {
  components: {
    Tab,
    Graph,
    Dialog: () => import("@/components/CustomReport/Dialog.vue"),
    RecyclerDataTable: () =>
      import("@/components/utility/RecyclerDataTable.vue"),
    TableButtons: () => import("@/components/CustomReport/TableButtons.vue"),
  },
  props: {
    selectedTemplate: {
      type: Object,
      default: function () {
        return {};
      },
    },
    activeFilter: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {
      loading: false,
      prgtInfo: {
        totalTimeBlock: 0,
        totalGen: 0,
        totalPr: 0,
        sourceName: "",
        criteria: "",
        refDevice: ""
      },
      nameIdMap: {},
      idNameMap: {},
      dataList: [],
      view: "table",
      options: {
        yAxis: [],
        xAxis: [],
        series: [],
      },
      primaryKey: "date_frame",
      isDownloadDialog: false,
      deviceTab: [],
      tableData: [],
      headers: [],
      timeout: {},
      activeTab: [],
    };
  },
  async mounted() {
    const actTab = this.selectedTemplate.loadFunctionSequence.map((el) => 0);
    actTab.pop();
    this.activeTab = actTab;
    for (let [
      index,
      item,
    ] of this.selectedTemplate.loadFunctionSequence.entries()) {
      try {
        await this.initialFunction(index, 0, item["func"], true);
      } catch (error) {
        console.error("Error processing item:", error);
      }
    }
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantInfo", "getGeneralState"]),
    isPrgtReport() {
    return this.selectedTemplate && this.selectedTemplate._id && 
           this.selectedTemplate._id.includes('PRGT');
    },
    prgtTableItems() {
      return [
        {
          key: "Date :",
          value: this.activeFilter.Date ? moment(this.activeFilter.Date.from).format("YYYY/MM/DD") : "",
          isNumber: false
        },
        {
          key: "Criteria :",
          value: `The average PoA irradiance in a time block is <span class="number--text">${this.prgtInfo.criteria}</span> in W/m².`,
          isNumber: false
        },
        {
          key: "No. of time blocks considered :",
          value: `${this.prgtInfo.totalTimeBlock}/57`,
          isNumber: true
        },
        {
          key: "Energy :",
          value: `<span class="number--text">${this.prgtInfo.totalGen.toFixed(
            2
          )} kWh</span> - Reference device: ${this.prgtInfo.sourceName.toUpperCase()}`,
          isNumber: true
        },
        {
          key: "Plant Temp.Corr. PR for the day :",
          value: `<span class="number--text">${
            this.prgtInfo.totalPr 
          } (%)</span> - The average Temp.Corr. PR for 15 mins interval where PoA irradiance is ${this.prgtInfo.criteria} W/m² is not explicitly removed.`,
          isNumber: true
        }
      ];
    },
    computedSeries() {
      const clonedSeries = _.cloneDeep(this.options.series);
      return clonedSeries.map((el) => {
        el.name = this.$t(el.name);
        return el;
      });
    },
    computedYAxis() {
      const clonedYAxis = _.cloneDeep(this.options.yAxis);
      return clonedYAxis.map((el) => {
        el.name = this.$t(el.name);
        return el;
      });
    },
    xAxis() {
      const newData = this.options.xAxis.map((date) => {
        return moment(date).format(this.activeFilter.DateFormat);
      });
      return newData;
    },
    selectedTemplateDescription() {
      return this.selectedTemplate.description
        ? this.selectedTemplate.description
        : "";
    },
    dataTitle() {
      if (this.selectedTemplate._id.includes("SlotReport")) {
        if (
          moment(this.activeFilter["Date"].from).isSame(
            moment(this.activeFilter["Date"].to)
          )
        ) {
          return `${this.$t(this.selectedTemplateDescription)} - ${
            this.activeFilter["Date"].from
          }`;
        } else {
          return `${this.$t(this.selectedTemplateDescription)} - ${
            this.activeFilter["Date"].from
          } ~ ${this.activeFilter["Date"].to}`;
        }
      }

      if (this.isPrgtReport) {
        return `${this.$t(this.selectedTemplateDescription)}`;
      }
      return `${this.$t(this.selectedTemplateDescription)} - ${this.$t(
        this.activeFilter["selectedRange"].text
      )}`;
    },
    currentTabDeviceNames() {
      return this.dataList[
        this.selectedTemplate.loadFunctionSequence.length - 2
      ];
    },
    filterValues() {
      const { from, to } = this.activeFilter.Date
        ? this.activeFilter.Date
        : { from: "", to: "" };
      return `${from}||${to}||${
        this.activeFilter.selectedRange && this.activeFilter.selectedRange.text
          ? this.activeFilter.selectedRange.text
          : ""
      }`;
    },
    dateFormattedTableData() {
      const tableData = _.cloneDeep(this.tableData);
      if (this.selectedTemplate._id.includes('SlotReport')) {
        return tableData
      }

      return tableData.map(el => {
        if (el[this.primaryKey] != 'Total') {
          const dateFormat = this.isPrgtReport ? "YYYY-MM-DD HH:mm" : this.activeFilter.DateFormat;
          el[this.primaryKey] = moment(el[this.primaryKey]).format(dateFormat);
        }
        return el
      })
    },
    computeReduceTableHeight() {
      if (!this.selectedTemplate || !this.selectedTemplate.loadFunctionSequence) {
        return 405;
      }
      const tabs = this.selectedTemplate.loadFunctionSequence.length - 1
      const tabHeight = 45;

      return 315 + (tabs * tabHeight);
    }
  },
  methods: {
    computeParam(initialTab = true) {
      const param = {
        plantId: this.getSelectedPlantId,
        plantName: this.getActivePlantInfo("plant_name"),
        template: this.selectedTemplate._id,
        dateFormat: this.activeFilter.DateFormat || "YYYY-MM-DD"
      };

      let interval = "", offsetLevel = "", startOffset = "", endOffset = "", intervalGap = "";

      if (!this.isPrgtReport) {
        const range = this.activeFilter.selectedRange || {};
        ({ interval, offsetLevel, startOffset, endOffset, intervalGap } = range);
      }

      const { from, to } = this.activeFilter.Date
        ? this.activeFilter.Date
        : { from: "", to: "" };
      const timezone = this.getActivePlantInfo("timezone");

      if (this.isPrgtReport) {
        if (this.activeFilter.Date && this.activeFilter.Date.from) {
          param.date = this.activeFilter.Date.from;
        } else {
          const today = momentTz()
            .tz(timezone)
            .format("YYYY-MM-DD");
          param.date = today;
        }

        return param;
      }

      if (interval === "custom") {
        const fromMonthStart =
          moment(from).startOf("month").format("YYYY-MM-DD") === from;
        const toMonthStart =
          moment(to).startOf("month").format("YYYY-MM-DD") === to;
        const isFullMonthsRange = moment(to).diff(from, "months") >= 1;

        if (fromMonthStart && toMonthStart && isFullMonthsRange) {
          param.type = "monthly";
          param.dateFrom = moment(from).format("YYYY-MM-00");
          param.dateTo = moment(to).subtract(1, "months").format("YYYY-MM-00");
        } else {
          param.dateFrom = from;
          param.dateTo = to;
        }
      } else if (interval === "daily") {
        param.dateFrom = momentTz()
          .tz(timezone)
          .subtract(startOffset, offsetLevel || "day")
          .startOf(offsetLevel || "day")
          .format("YYYY-MM-DD");
        param.dateTo = momentTz()
          .tz(timezone)
          .subtract(endOffset, offsetLevel || "day")
          .endOf(offsetLevel || "day")
          .format("YYYY-MM-DD");
      } else if (interval === "monthly") {
        const format = initialTab ? "YYYY-MM-00" : "YYYY-MM-DD";
        param.dateFrom = momentTz()
          .tz(timezone)
          .subtract(startOffset, offsetLevel)
          .startOf(offsetLevel)
          .format(format);
        param.dateTo = momentTz()
          .tz(timezone)
          .subtract(endOffset, offsetLevel)
          .endOf(offsetLevel)
          .format(format);
      } else if (interval === "hourly" || interval === "minute") {
        param.dateFrom = from;
        param.dateTo = to;
        param.interval = intervalGap || 1;
      }

      if (this.selectedTemplate._id.includes("SlotReport")) {
        param.dateFrom = this.activeFilter.Date.from;
        param.dateTo = this.activeFilter.Date.to;
      }

      return param;
    },
    async tabChange(values) {
      const { level, tab } = values;

      for (
        let i = level + 1;
        i < this.selectedTemplate.loadFunctionSequence.length - 1;
        i++
      ) {
        this.$set(this.dataList, i, undefined);
      }
      this.$set(this.activeTab, level, tab);
      this.activeTab[level] = tab;
      this.$emit("loading:report", this.loading);

      if (this.selectedTemplate._id.includes("GenReport")) {
        if (tab == 0) {
          this.view = "graph";
        } else {
          this.view = "table";
        }
      } else {
        for (
          let i = level + 1;
          i <= this.selectedTemplate.loadFunctionSequence.length;
          i++
        ) {
          try {
            // Perform an asynchronous operation here
            if (i == level + 1) {
              await this.initialFunction(
                i,
                tab,
                this.selectedTemplate.loadFunctionSequence[i]["func"]
              );
            } else {
              await this.initialFunction(
                i,
                0,
                this.selectedTemplate.loadFunctionSequence[i]["func"]
              );
            }
          } catch (error) {
            console.error("Error processing item:", error);
          }
        }
      }
    },
    async initialFunction(level, activeTab, funcName) {
      const functionMap = {
        loadStaticTab: this.loadStaticTab,
        loadGenerationReport: this.loadGenerationReport,

        loadSummaryTab: this.loadPlantSummaryReportTabs,
        loadSummaryData: this.loadSummaryData,
        loadPrgtTab: this.loadPlantPrgtReportTabs,
        loadPrgtData: this.loadPrgtData,
        loadPlantSlotData: this.loadPlantSlotData,
        loadDeviceSlotReportData: this.loadDeviceSlotReportData,

        loadBlock: this.fetchAllBlocks,
        loadBlockEquipments: this.loadBlockEquipments,
        loadBlockDevice: this.loadBlockDevice,
        loadBlockReportData: this.loadBlockReportData,
        loadBlockSlotReportData: this.loadBlockSlotReportData,

        loadEquipment: this.loadAllEquipment,
        loadDevice: this.loadDevices,
        loadDeviceReportData: this.loadDeviceReportData,
        loadPerformanceData: this.loadPerformanceData
      };

      try {
        await new Promise((resolve, reject) => {
          setTimeout(() => {
            resolve(functionMap[funcName](level, activeTab));
          }, 1000);
        });
      } catch (error) {
        console.error(error);
      }
    },
    loadStaticTab() {
      this.$set(this.dataList, 0, ["GRAPHICAL REPORTS", "TABULAR REPORTS"]);
    },
    async loadPlantSummaryReportTabs(level) {
      this.loading = true;
      try {
        const summaryTabs = await CustomReport.fetchAllEquipments({
          plantId: this.getSelectedPlantId,
          template: this.selectedTemplate._id,
        });
        this.loading = false;
        this.$set(this.dataList, level, [...summaryTabs.data]);
      } catch (err) {
        this.$set(this.dataList, level, []);
        this.loading = false;
      }
    },
    async loadPlantPrgtReportTabs(level) {
      this.loading = true;
      try {
        const prgtTabs = await CustomReport.fetchAllEquipments({
          plantId: this.getSelectedPlantId,
          template: this.selectedTemplate._id
        });
        this.loading = false;
        this.$set(this.dataList, level, [...prgtTabs.data]);
      } catch (err) {
        this.$set(this.dataList, level, []);
        this.loading = false;
      }
    },
    async loadSummaryData(level, activeTab) {
      try {
        let param = this.computeParam(false);
        param["device"] = Array.from(this.dataList)[0][activeTab];
        const summaryReport = await CustomReport.fetchPlantSummary(param);
        this.loading = false;
        const { data } = summaryReport;
        this.tableData = data.data;
        this.headers = data.header;
      } catch (err) {
        this.loading = false;
      }
    },
    async loadPrgtData(level) {
      try {
        const plantId = this.getSelectedPlantId;
        let param = this.computeParam(false);
        const allDevices = Array.from(this.dataList)[0];

        const config = await CustomReport.fetchPlantPrgtConfig(plantId);
        const refDevice = config.data.ref_device;
        
        this.prgtInfo.refDevice = refDevice;
        this.prgtInfo.criteria = config.data.criteria;
        this.prgtInfo.sourceName = config.data.ref_device;

        param.devices = allDevices.filter(
          device => device === "weather station" || device === refDevice
        );
        const prgtReport = await CustomReport.fetchPlantPrgt(param);
        const { data } = prgtReport;

        this.loading = false;
        this.tableData = data.data;
        this.headers = data.header || [];

        this.tableData = this.tableData.map(row => {
          return {
            ...row,
            temp_pr: row.temp_pr || row["Temp Corrected PR (%)"] || 0,
            explicit_removal: row.explicit_removal >=0 
              ? row.explicit_removal
              : row["Explicit Removal"] >= 0 
                ? row["Explicit Removal"] 
                : null
          };
        });

        let validTimeBlocks = 0;
        const timeBlockRemarks = {};
        this.tableData.forEach(row => {
          const timeblock = row.date_frame || row.sensor_time;
          const explicitRemoval = row.explicit_removal;
          if (timeblock) {
            if (explicitRemoval === 0) validTimeBlocks++;
            if (explicitRemoval === 1) timeBlockRemarks[timeblock] = "";
          }
        });

        this.tableData = this.tableData.map(row => {
          const timeblock = row.date_frame || row.sensor_time;
          if (timeblock) {
            return {
              ...row,
              remarks: timeBlockRemarks[timeblock] !== undefined ? timeBlockRemarks[timeblock] : row.remarks || ""
            };
          }
          return row;
        });

        const lastRow = this.tableData[this.tableData.length - 1];

        const genKey =
          refDevice === "inverter"
            ? "AC Generation (kWh)"
            : ["mfm", "energy meter"].includes(refDevice)
            ? "Active Export Cumm. (kWh)"
            : null;

        const rawGen = lastRow[genKey];

        this.prgtInfo.totalGen = !isNaN(parseFloat(rawGen))
          ? parseFloat(rawGen)
          : parseFloat(lastRow.total_generation || 0) || 0;

        this.prgtInfo.totalPr = parseFloat(lastRow["Temp Corrected PR (%)"] || 0);
        this.prgtInfo.totalTimeBlock = validTimeBlocks;
      } catch (err) {
        this.loading = false;
        console.error("Error loading PRGT data:", err);
      }
    },
    async loadPerformanceData(level, activeTab) {
      this.loading = true;

      try {
        const param = this.computeParam();
        param.device = this.dataList[0][this.activeTab[0]];
        const deviceName = this.dataList[1][this.activeTab[1]];
        param.deviceId = this.nameIdMap[deviceName];
        param.deviceName = deviceName;

        this.primaryKey = "date_frame";
        const result = await CustomReport.fetchPerformanceReport(param)
        this.loading = false;
        const { data } = result;
        this.tableData = data.data;
        this.headers = data.header;
      } catch(err) {
        this.loading = false;
        this.tableData = [];
        this.headers = [];
      }
    },
    async loadPlantSlotData(level, activeTab) {
      try {
        let param = this.computeParam(false);
        param["device"] = Array.from(this.dataList)[0][activeTab];
        param.computeAt = this.selectedTemplate.level;

        this.primaryKey = "date_frame";
        const summaryReport = await CustomReport.fetchSlotWiseReportData(param);
        this.loading = false;
        const { data } = summaryReport;
        this.tableData = data.data;
        this.headers = data.header;
      } catch (err) {
        this.loading = false;
      }
    },
    async loadDeviceSlotReportData() {
      this.loading = true;
      try {
        const param = this.computeParam();
        param.device = this.dataList[0][this.activeTab[0]];
        const deviceName = this.dataList[1][this.activeTab[1]];
        param.deviceId = this.nameIdMap[deviceName];
        param.deviceName = deviceName;
        param.computeAt = this.selectedTemplate.level;

        this.primaryKey = "date_frame";
        const result = await CustomReport.fetchSlotWiseReportData(param);
        const { data } = result;
        this.tableData = data.data;
        this.headers = data.header;
        this.loading = false;
      } catch (err) {
        this.loading = false;
      }
    },
    async fetchAllBlocks(level) {
      this.loading = true;
      try {
        const blockList = await CustomReport.fetchBlock({
          plantId: this.getSelectedPlantId,
        });

        this.loading = false;
        this.$set(this.dataList, level, [...blockList.data]);
      } catch (err) {
        this.loading = false;
      }
    },
    async loadAllEquipment(level) {
      this.loading = true;
      try {
        const result = await CustomReport.fetchAllEquipments({
          plantId: this.getSelectedPlantId,
          template: this.selectedTemplate._id,
        });
        this.loading = false;
        this.$set(this.dataList, level, [...result.data]);
      } catch (err) {
        this.loading = false;
      }
    },
    async loadDevices(level) {
      this.loading = true;
      try {
        const param = {};
        param["plantId"] = this.getSelectedPlantId;
        param.device = this.dataList[0][this.activeTab[0]];

        if (this.dataList[level - 1] !== undefined) {
          param["blockId"] =
            this.dataList[level - 1][this.activeTab[level - 1]].id;
        }

        const result = await CustomReport.fetchEquipmentWiseDevice(param);
        this.loading = false;
        this.nameIdMap = {};
        this.idNameMap = result.data;
        Object.keys(result.data).forEach((key) => {
          this.nameIdMap[result.data[key]] = key;
        });
        this.$set(this.dataList, 1, Object.values(result.data));
      } catch (err) {
        this.loading = false;
      }
    },
    async loadBlockDevice() {
      this.loading = true;
      try {
        const param = {};
        param["plantId"] = this.getSelectedPlantId;
        param["blockId"] = this.dataList[0][this.activeTab[0]].id;
        param["device"] = this.dataList[1][this.activeTab[1]];
        const equipmentList = await CustomReport.fetchBlockWiseDevice(param);

        this.loading = false;
        this.nameIdMap = {};
        this.idNameMap = equipmentList.data;
        Object.keys(equipmentList.data).forEach((key) => {
          this.nameIdMap[equipmentList.data[key]] = key;
        });
        this.$set(this.dataList, 2, Object.values(equipmentList.data));
      } catch (err) {
        this.loading = false;
      }
    },
    async loadBlockEquipments(level) {
      this.loading = true;
      try {
        const param = {};
        param["plantId"] = this.getSelectedPlantId;
        param["blockId"] = this.dataList[0][this.activeTab[0]].id;
        param["template"] = this.selectedTemplate._id;
        const equipmentList = await CustomReport.fetchBlockEquipment(param);
        this.loading = false;
        const devicesAvail = equipmentList.data.map((el) => el["device_name"]);
        this.$set(this.dataList, level, [...new Set(devicesAvail)]);
      } catch (err) {
        this.loading = false;
      }
    },
    async loadDeviceReportData() {
      this.loading = true;

      try {
        const param = this.computeParam();
        param.device = this.dataList[0][this.activeTab[0]];
        const deviceName = this.dataList[1][this.activeTab[1]];
        param.deviceId = this.nameIdMap[deviceName];
        param.deviceName = deviceName;

        this.primaryKey = param.interval == 1 ? "Date" : "date_frame";
        const result = await CustomReport.fetchDeviceReport(param);
        this.loading = false;
        const { data } = result;
        this.tableData = data.data;
        this.headers = data.header;
      } catch (err) {
        this.loading = false;
        this.tableData = [];
        this.headers = [];
      }
    },
    async loadBlockReportData() {
      this.loading = true;

      try {
        const param = this.computeParam();
        const deviceName = this.dataList[2][this.activeTab[2]];
        param.device = this.dataList[1][this.activeTab[1]];
        param.deviceId = this.nameIdMap[deviceName];
        param.deviceName = deviceName;
        this.primaryKey = param.interval == 1 ? "Date" : "date_frame";

        const result = await CustomReport.fetchDeviceReport(param);
        this.loading = false;
        const { data } = result;

        this.tableData = data.data;
        this.headers = data.header;
      } catch (err) {
        this.loading = false;
        this.tableData = [];
        this.headers = [];
      }
    },
    async loadBlockSlotReportData() {
      this.loading = true;

      try {
        const param = this.computeParam();
        const deviceName = this.dataList[2][this.activeTab[2]];
        param.device = this.dataList[1][this.activeTab[1]];
        param.deviceId = this.nameIdMap[deviceName];
        param.deviceName = deviceName;
        param.computeAt = this.selectedTemplate.level;
        this.primaryKey = "date_frame";

        const result = await CustomReport.fetchSlotWiseReportData(param);
        this.loading = false;
        const { data } = result;

        this.tableData = data.data;
        this.headers = data.header;
      } catch (err) {
        this.loading = false;
        this.tableData = [];
        this.headers = [];
      }
    },
    async loadGenerationReport(level) {
      this.view = this.activeTab[level] == 1 ? "table" : "graph";
      this.loading = true;

      try {
        const param = this.computeParam();
        param["device"] = this.selectedTemplate.device;
        param["computeAt"] = this.selectedTemplate.level;
        const result = await CustomReport.fetchData(param);
        this.loading = false;
        const data = result.data;

        this.graphTransLogic(data);
        this.tableTransLogic(data);
      } catch (err) {
        this.loading = false;
        this.options = {
          yAxis: [],
          xAxis: [],
          series: [],
        };
        this.tableData = [];
        this.headers = [];
      }
    },
    tableTransLogic(data) {
      let tableData = [];
      let headers = [
        {
          key: "Date",
          value: "Date",
        },
      ];
      this.primaryKey = "Date";

      const sumObj = {};

      // Iterate through each equipment type (e.g., inverter, weather station)
      Object.keys(data).forEach((equipment) => {
        // Iterate through each device under the current equipment type
        Object.keys(data[equipment]).forEach((deviceName, index) => {
          headers.push({ key: deviceName, value: deviceName });

          data[equipment][deviceName].forEach((entry, i) => {
            const value =
              entry.value === null
                ? null
                : parseFloat(Number(entry.value).toFixed(2));

            // Create the initial array of objects with date using the first device data
            if (tableData[i] == undefined) {
              const dateEntry = { Date: entry.dates };
              dateEntry[deviceName] = value;
              tableData.push(dateEntry);
            } else {
              tableData[i][deviceName] = value;
            }

            if (!sumObj[deviceName]) {
              sumObj[deviceName] = 0;
            }
            sumObj[deviceName] += value;
          });

          if (deviceName == "POA (W/m²)") {
            sumObj[deviceName] = parseFloat(
              sumObj[deviceName] / data[equipment][deviceName].length
            ).toFixed(2);
          } else {
            sumObj[deviceName] = parseFloat(sumObj[deviceName]).toFixed(2);
          }
        });
      });

      // Add the total row
      sumObj["Date"] = "Total";
      tableData.push(sumObj);
      this.tableData = [...tableData];
      this.headers = headers;
    },
    graphTransLogic(data) {
      const selectedDevice = this.selectedTemplate.device;
      const series = [];
      const yAxis = [{
          name: "Generation (kWh)",
          type: "value",
        position: "left",
      }]

      const createSeriesStructure = (deviceName, type, dataPoints) => ({
        name: deviceName,
        type: type,
        large: true,
        data: dataPoints,
      });

      if (data[selectedDevice]) {
        const deviceData = data[selectedDevice];

        Object.keys(deviceData).forEach((deviceName, index) => {
          const seriesData = deviceData[deviceName].map((el) => el.value);
          const seriesStructure = createSeriesStructure(
            deviceName,
            "bar",
            seriesData
          );

          if (index === 0) {
            this.options.xAxis = deviceData[deviceName].map((el) => el.dates);
          }

          series.push(seriesStructure);
        });
      }

      // Process weather station data
      if (data["weather station"] && !_.isEmpty(data["weather station"])) {
        const weatherStationData = data["weather station"];

        Object.keys(weatherStationData).forEach((deviceName) => {
          const seriesData = weatherStationData[deviceName].map(
            (el) => el.value
          );
          const seriesStructure = createSeriesStructure(
            deviceName,
            "line",
            seriesData
          );

          if (deviceName.includes("Insolation")) {
            yAxis.push({
              name: deviceName,
              type: "value",
              position: "right",
            })
            seriesStructure["yAxisIndex"] = 1;
          }

          series.push(seriesStructure);
        });
      }

      this.options.series = series;
      this.options.yAxis = yAxis;
    },
    initiateDownload() {
      if (
        !this.isPrgtReport &&
        !this.selectedTemplate._id.includes("GenReport") &&
        !this.selectedTemplate._id.includes("SummaryReport")
      ) {
        this.isDownloadDialog = true;
      } else {
        this.downloadReport();
      }
    },
    refreshTable() {
      this.initialFunction(
        this.selectedTemplate.loadFunctionSequence.length - 2,
        this.activeTab[this.selectedTemplate.loadFunctionSequence.length - 2],
        this.selectedTemplate.loadFunctionSequence[
          this.selectedTemplate.loadFunctionSequence.length - 1
        ]["func"]
      );
    },
    downloadReport(payloadIdList = []) {
      this.isDownloadDialog = false;
      this.loading = true;
      const param = this.computeParam(false);
      if (param.template.includes("SlotReport")) {
        param.computeAt = this.selectedTemplate.level;
      }
      let reportName = "",
        functionName = "",
        paramB64 = "";

      const template = this.selectedTemplate._id;
      const loadSequenceLength =
        this.selectedTemplate.loadFunctionSequence.length;
      const activePlantName = this.getActivePlantInfo("plant_name");

      const processDeviceReport = () => {
        const idList = [];
        const idNameMap = {};

        const selectedIdList =
          payloadIdList.length > 0
            ? [...payloadIdList]
            : this.dataList[loadSequenceLength - 2];
        const deviceType =
          this.dataList[loadSequenceLength - 3][
          this.activeTab[loadSequenceLength - 3]
        ];

        selectedIdList.forEach((e) => {
          idList.push(this.nameIdMap[e]);
          idNameMap[this.nameIdMap[e]] = this.idNameMap[this.nameIdMap[e]];
        });

        param.idNameMap = idNameMap;
        param.idList = idList;
        param.device = deviceType;

        paramB64 = btoa(JSON.stringify(param));
        reportName = `${activePlantName} - ${
          loadSequenceLength == 3 ? "device" : "block"
        } level ${param.device ? param.device : ""}`;
      };

      const processPlantSummaryReport = () => {
        param.deviceTypeList = this.dataList[0];
        paramB64 = btoa(JSON.stringify(param));
        reportName = `${activePlantName} - ${
          param.template ? param.template : ""
        }`;
      };

      const processPlantSlotReport = () => {
        param.device = this.dataList[0][this.activeTab[0]];
        paramB64 = btoa(JSON.stringify(param));
        reportName = `${activePlantName} - ${
          param.template ? param.template : ""
        }`;
      };

      const processPlantPRGTReport = () => {
        const allDevices = Array.from(this.dataList)[0];
        param.deviceTypeList = allDevices.filter(
          device => device === "weather station" || device === this.prgtInfo.refDevice
        );
        param.description = this.prgtTableItems;
        paramB64 = btoa(JSON.stringify(param));
        reportName = `${activePlantName} - ${
          param.template ? param.template : ""
        }`;
      };

      const processGenerationReport = () => {
        param.device = this.selectedTemplate.device;
        param.computeAt = this.selectedTemplate.level;
        paramB64 = btoa(JSON.stringify(param));
        reportName = `${activePlantName} - ${
          param.template ? param.template : ""
        }`;
      };

      switch (template) {
        case "deviceLevelDeviceReport":
        case "blockLevelDeviceReport":
          processDeviceReport();
          functionName = "downloadDeviceReport";
          break;
        case "plantSummaryReport":
          processPlantSummaryReport();
          functionName = "downloadPlantSummary";
          break;
        case "plantPRGTReport":
          processPlantPRGTReport();
          functionName = "downloadPlantPrgt";
          break;
        case "deviceLevelPerformanceReport":
          processDeviceReport();
          functionName = "downloadPerformanceReport";
          break;
        case "blockLevelSlotReport":
        case "deviceLevelSlotReport":
          processDeviceReport();
          functionName = "downloadSlotReport";
          break;
        case "plantLevelSlotReport":
          processPlantSlotReport();
          functionName = "downloadSlotReport";
          break;
        default:
          processGenerationReport();
          functionName = "downloadGenerationReport";
      }

      CustomReport[functionName](paramB64, reportName)
        .then(() => {
          this.loading = false;
          this.chipItems = [];
        })
        .catch((err) => {
          this.loading = false;
        });
    },
  },
  watch: {
    loading() {
      this.$emit("loading:report", this.loading);
    },
    filterValues() {
      this.initialFunction(
        this.selectedTemplate.loadFunctionSequence.length - 2,
        this.activeTab[this.selectedTemplate.loadFunctionSequence.length - 2],
        this.selectedTemplate.loadFunctionSequence[
          this.selectedTemplate.loadFunctionSequence.length - 1
        ]["func"]
      );
    },
  },
};
</script>

<style scoped>
.table td,
.table th {
  border: 1px solid #bababa !important;
}
</style>